import { create } from 'zustand';
import { useQueryClient } from '@tanstack/react-query';

import { getActivePrincipalId, setActivePrincipalId } from 'src/api/principals';
import { setPrincipalHeader } from 'src/api/client';

// Reactive mirror of the persisted active-principal selection (REQ-20). The
// source of truth for `withPrincipal` is still localStorage (a leaf, no app
// deps); this store just lets the banner + switcher re-render on change and
// centralises the "write localStorage + set request header" side-effects.
type TPrincipalStore = {
  activePrincipalId: string | null;
  setActive: (id: string | null) => void;
};

export const usePrincipalStore = create<TPrincipalStore>((set) => ({
  activePrincipalId: getActivePrincipalId(),
  setActive: (id) => {
    const normalized = id && id !== 'self' ? id : null;
    setActivePrincipalId(normalized); // persist
    setPrincipalHeader(normalized); // X-On-Behalf-Of header
    set({ activePrincipalId: normalized });
  },
}));

export const useActivePrincipalId = () =>
  usePrincipalStore((s) => s.activePrincipalId);

/**
 * Switch (or clear) the acting-as principal: persists + sets the header, then
 * invalidates ALL queries so every principal-scoped surface refetches under the
 * new principal. Pass null (or 'self') to return to your own account.
 */
export const useSwitchPrincipal = () => {
  const qc = useQueryClient();
  const setActive = usePrincipalStore((s) => s.setActive);
  return (id: string | null) => {
    setActive(id);
    qc.invalidateQueries();
  };
};
